import { GetTemplateRolesResponseDto } from '@modusign/openapi-client-template/model';
import { useSelector } from 'react-redux';

import { getHasActiveSubscription } from '@app/features/subscription/state';
import { useCreateTemplateAvailability } from '@app/features/template';
import {
  TemplateResourceRoleResponseOptions,
  useTemplateResourceRole,
} from '@app/features/template/hooks/useTemplateResourceRole';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import useUsageCount from '@app/hooks/useUsageCount';
import { useCurrentMemberGroupList, useCurrentUserMemberId } from '@app/hooks/workspace';
import { getNetPermission } from '@app/libs/permission/getNetPermission';
import {
  getHasBulkRequestPermission,
  getHasContractLinkPermission,
  getHasDocumentPermission,
  getHasTemplatePermission,
} from '@app/store/user/authority/selectors';

interface UseTemplatePermissionProps {
  resourceRoleQueryOption: {
    templateId: string;
    options?: TemplateResourceRoleResponseOptions;
  };
}

export default function useTemplatePermission(param?: UseTemplatePermissionProps) {
  const hasTemplateAuthorityPermission = useSelector(getHasTemplatePermission);
  const hasDocumentAuthorityPermission = useSelector(getHasDocumentPermission);
  const hasBulkRequestAuthorityPermission = useSelector(getHasBulkRequestPermission);
  const hasContractLinkAuthorityPermission = useSelector(getHasContractLinkPermission);
  // 내부 결재는 요금제 구독 상태이면 모두 가능합니다.
  const hasActiveSubscription = useSelector(getHasActiveSubscription);

  const { signingRequestCount } = useUsageCount();

  const currentMemberId = useCurrentUserMemberId();
  const currentGroupIds = useCurrentMemberGroupList().map((g) => g.id);

  const query = useTemplateResourceRole(
    {
      templateId: param?.resourceRoleQueryOption.templateId,
    },
    {
      ...(param?.resourceRoleQueryOption?.options || { enabled: false }),
    }
  );
  const { templateLimit, templateUsages } = useCreateTemplateAvailability();
  const { permissionMap, isLoading, isError } = useMemberPermissions();

  const createTemplatePermission = getNetPermission({
    hasAuthorityPermission: hasTemplateAuthorityPermission,
    hasRolePermission: permissionMap.CREATE_TEMPLATE,
    resourceUsages: [{ limit: templateLimit, usages: templateUsages, resourceName: '템플릿' }],
  });

  const requestSigningPermission = getNetPermission({
    hasAuthorityPermission: hasDocumentAuthorityPermission,
    hasRolePermission: permissionMap.CREATE_DOCUMENT_WITH_TEMPLATE,
    resourceUsages: [
      { limit: templateLimit, usages: templateUsages, resourceName: '템플릿', onlyExceeded: true },
      {
        limit: signingRequestCount.limit,
        usages: signingRequestCount.usage,
        resourceName: '서명 요청',
      },
    ],
  });

  /**
   * 공유 중인 그룹이나, 다른 멤버가 있는지 확인합니다.
   */
  const checkSharedResource = (data: GetTemplateRolesResponseDto) =>
    data.templateRoles.filter(
      (role) => role.group || (role.member && role.member.id !== currentMemberId)
    ).length > 0;

  /**
   * 현재 유저에게 편집자 역할이 있는지 확인합니다.
   */
  const checkEditorRole = (data: GetTemplateRolesResponseDto) =>
    data.templateRoles.some(
      (role) =>
        role.name === 'EDITOR' &&
        ((role.member && role.member.id === currentMemberId) ||
          (role.group && currentGroupIds.includes(role.group.id)))
    );

  /*
  템플릿 생성, 수정, 복제 외 나머지 기능은 요금제 템플릿 authority가 불필요합니다.
  notion: https://www.notion.so/modusign/58f504dc31e94236951f25a4f3112443?pvs=4#f02fb37b99734683a88b7a179deee6ed
  */
  const editTemplatePermission = getNetPermission({
    hasAuthorityPermission: hasTemplateAuthorityPermission,
    hasRolePermission: !!(
      permissionMap.UPDATE_TEMPLATE ||
      (query.data && checkEditorRole(query.data))
    ),
    resourceUsages: [
      { limit: templateLimit, usages: templateUsages, resourceName: '템플릿', onlyExceeded: true },
    ],
  });

  const deleteTemplatePermission = getNetPermission({
    hasAuthorityPermission: true,
    hasRolePermission:
      permissionMap.DELETE_TEMPLATE || !!(query.data && checkEditorRole(query.data)),
  });

  const assignTemplatePermission = getNetPermission({
    hasAuthorityPermission: true,
    hasRolePermission: permissionMap.ASSIGN_TEMPLATE,
  });

  const bulkRequestWithTemplatePermission = getNetPermission({
    hasAuthorityPermission: hasBulkRequestAuthorityPermission,
    hasRolePermission: permissionMap.MANAGE_BULK_REQUEST,
    resourceUsages: [
      { limit: templateLimit, usages: templateUsages, resourceName: '템플릿', onlyExceeded: true },
    ],
  });

  const contractLinkWithTemplatePermission = getNetPermission({
    hasAuthorityPermission: hasContractLinkAuthorityPermission,
    hasRolePermission: permissionMap.MANAGE_CONTRACT_LINK,
    resourceUsages: [
      { limit: templateLimit, usages: templateUsages, resourceName: '템플릿', onlyExceeded: true },
    ],
  });

  const createApprovalWithTemplatePermission = getNetPermission({
    hasAuthorityPermission: hasActiveSubscription,
    hasRolePermission:
      permissionMap.MANAGE_APPROVAL && !permissionMap.CREATE_DOCUMENT_WITH_TEMPLATE,
    resourceUsages: [
      { limit: templateLimit, usages: templateUsages, resourceName: '템플릿', onlyExceeded: true },
      {
        limit: signingRequestCount.limit,
        usages: signingRequestCount.usage,
        resourceName: '서명 요청',
      },
    ],
  });

  return {
    createTemplatePermission,
    editTemplatePermission,
    editSharedTemplatePermission:
      !!query.data && checkSharedResource(query.data) && editTemplatePermission.isPermitted,
    deleteTemplatePermission,
    requestSigningPermission,
    assignTemplatePermission,
    bulkRequestWithTemplatePermission,
    contractLinkWithTemplatePermission,
    createApprovalWithTemplatePermission,
    isLoading: query.isFetching || isLoading,
    isError: query.isError || isError,
  };
}
